import { z } from "zod";
import { zPhoneNumber } from "../validations/phone-number-validator";
import { AgentHoursSchema, CustomPrepTimeHoursSchema } from "../modules/hours";
import { MenuConfigFormSchema } from "../modules/menu";
import { AgentType } from "@/types/base";

const HolidayTimeSlotSchema = z.object({
  open: z.string(),
  close: z.string(),
});

const HolidayHoursSchema = z.object({
  name: z.string(),
  dateFrom: z.string(),
  dateTo: z.string(),
  hours: z.array(HolidayTimeSlotSchema),
  hoursOrdering: z.array(HolidayTimeSlotSchema).nullable(),
  greetingMessageOpen: z.string().optional(),
  greetingMessageClosed: z.string().optional(),
});

const HolidayHoursConfigSchema = z.object({
  enabled: z.boolean(),
  holidays: z.array(HolidayHoursSchema),
});

const PaymentModuleConfigSchema = z.union([
  z.object({
    provider: z.literal("none"),
  }),
  z.object({
    provider: z.literal("stripe"),
    strategy: z.enum(["required", "optional", "not_allowed"]),
    requiredAboveThreshold: z.number().optional(),
    requiredWhenFutureOrder: z.boolean().optional(),
    requireTip: z.boolean(),
  }),
]);

const HUBSPOT_DEAL_ID_REGEX = /^\d{11}$/;

export const BaseAgentFormSchema = z.object({
  // core
  id: z.string().optional(),
  name: z.string().trim().min(1, "Name is required."),
  type: z.enum(Object.values(AgentType) as [AgentType, ...AgentType[]]),
  placeId: z.string(),
  timezone: z.string().nullable(),
  hours: AgentHoursSchema.nullable(),
  hoursOrdering: AgentHoursSchema.nullable().optional(),
  accountId: z.string().min(1, "Organization is required").optional(),
  phoneNumber: zPhoneNumber.optional(),

  // twilio
  twilioOverride: z
    .object({
      enabled: z.boolean(),
      sid: z.string().optional(),
      auth: z.string().optional(),
    })
    .refine(
      (data) => {
        if (!data.enabled) return true;
        return (
          data.sid !== undefined &&
          typeof data.sid === "string" &&
          data.sid.trim().length >= 1
        );
      },
      {
        message: "Twilio SID is required when Twilio Override is enabled",
        path: ["sid"],
      },
    )
    .refine(
      (data) => {
        if (!data.enabled) return true;
        return (
          data.auth !== undefined &&
          typeof data.auth === "string" &&
          data.auth.trim().length >= 1
        );
      },
      {
        message:
          "Twilio Auth Token is required when Twilio Override is enabled",
        path: ["auth"],
      },
    )
    .nullable()
    .optional(),
  smsPhoneNumber: zPhoneNumber.nullable().optional(),

  // direct prompting
  greetingMessageOpen: z
    .string()
    .trim()
    .min(1, "Greeting message is required."),
  greetingMessageOrderingClosed: z
    .string()
    .trim()
    .min(1, "Greeting message is required."),
  greetingMessageClosed: z
    .string()
    .trim()
    .min(1, "Greeting message is required."),
  hubspotDealId: z
    .string()
    .trim()
    .nullable()
    .optional()
    .refine(
      (value) => !value || HUBSPOT_DEAL_ID_REGEX.test(value),
      "HubSpot Deal ID must be exactly 11 digits",
    ),
  prompt: z.string().nullable().optional(),

  // action triggers
  fallbackPhoneNumber: zPhoneNumber.nullable().optional(),
  transfers: z
    .array(
      z
        .object({
          key: z.string(),
          value: z.union([z.string(), z.array(z.string())]),
        })
        .refine(
          (obj) => {
            if (!obj.key && !obj.value) return true;
            if (!obj.key.trim()) return false;
            if (Array.isArray(obj.value)) {
              return obj.value.every((v) => zPhoneNumber.safeParse(v).success);
            }
            return zPhoneNumber.safeParse(obj.value).success;
          },
          { message: "Please enter a valid trigger and phone number" },
        ),
    )
    .optional(),
  messages: z
    .array(
      z
        .object({
          key: z.string(),
          value: z.string(),
        })
        .refine(
          (obj) => {
            if (!obj?.key && !obj?.value) return true;
            if (!obj?.key?.trim()) return false;
            return z.string().url().safeParse(obj?.value).success;
          },
          { message: "Please enter a valid trigger and URL" },
        ),
    )
    .optional(),

  // geographic consent
  twoPartyConsent: z.boolean().nullable(),

  streamConnectedAt: z.string().nullable().optional(),

  // POS Providers
  pos: z
    .enum([
      "toast",
      "square",
      "clover",
      "skytab",
      "stream",
      "kwick",
      "loman",
      "spoton",
      "ncr_aloha_essentials",
      "foodtec",
      "toast-legacy",
    ])
    .nullable()
    .optional(),

  // Toast
  toastUrl: z.string().nullable().optional(),
  toastLocationId: z.string().nullable().optional(),
  toastMenuGuids: z.array(z.string()).nullable().optional(),
  toastPickupDiningOptionGuid: z.string().nullable().optional(),
  toastPickupServiceCharges: z
    .array(
      z.object({
        id: z.string(),
        type: z.enum(["OPEN", "FIXED", "PERCENT"]),
      }),
    )
    .nullable()
    .optional(),
  toastDeliveryDiningOptionGuid: z.string().nullable().optional(),
  toastDeliveryServiceCharges: z
    .array(
      z.object({
        id: z.string(),
        type: z.enum(["OPEN", "FIXED", "PERCENT"]),
      }),
    )
    .nullable()
    .optional(),
  toastRevenueCenterGuid: z.string().nullable().optional(),
  toastPaymentTypeGuid: z.string().nullable().optional(),
  toastPollStockUpdates: z.boolean().nullable().optional(),
  // Square
  squareMerchantId: z.string().nullable().optional(),
  squareLocationId: z.string().nullable().optional(),
  squareAccessToken: z.string().nullable().optional(),
  squareRefreshToken: z.string().nullable().optional(),
  // Clover
  cloverMerchantId: z.string().nullable().optional(),
  cloverAccessToken: z.string().nullable().optional(),
  cloverRefreshToken: z.string().nullable().optional(),
  cloverOrderTypeId: z.string().nullable().optional(),
  // Kwick
  kwickMerchantKey: z.string().nullable().optional(),
  kwickMerchantToken: z.string().nullable().optional(),
  // Stream
  subprovider: z
    .enum(["toast", "clover", "square", "skytab", "ncr_aloha_essentials"])
    .nullable()
    .optional(),
  streamUrl: z.string().nullable().optional(), // dynamic prep time toast url
  streamLocationId: z.string().nullable().optional(),
  // Skytab
  skytabLocationId: z.string().nullable().optional(),
  skytabOrderTypeRef: z.string().nullable().optional(),
  // SpotOn
  spotOnLocationId: z.string().nullable().optional(),
  spotOnMenuId: z.string().nullable().optional(),
  // Foodtec
  foodtecBaseUrl: z.string().nullable().optional(),
  foodtecMenuAuth: z.string().nullable().optional(),
  foodtecCheckoutAuth: z.string().nullable().optional(),
  foodtecOrderAuth: z.string().nullable().optional(),
  foodtecOrderTypes: z.array(z.string()).nullable().optional(),

  // Menu
  menuModuleConfig: MenuConfigFormSchema.nullable().optional(),

  // Ordering
  orderingOrderUntilClosed: z.boolean().nullable().optional(),
  orderingAllowScheduledOrders: z
    .enum(["always", "never", "normal", "morning"])
    .nullable()
    .optional(),
  orderingShouldTotal: z.boolean().optional(),
  orderingExactTimeEstimate: z.boolean().optional(),
  orderingInstantSubmit: z.boolean().nullable().optional(),
  orderingMinLeadTime: z.number().nullable().optional(),
  orderingMinTotalThreshold: z.number().nullable().optional(),
  orderingMaxTotalThreshold: z.number().nullable().optional(),
  orderingPrepTime: z.number().nullable().optional(),
  // Toast Ordering
  orderingDynamicPrepTime: z.boolean().nullable().optional(),
  // [Toast, Loman] Ordering
  orderingTaxRate: z.number().nullable().optional(),
  // [Stream, Foodtec, Clover, SpotOn, Square, Skytab, Toast] Ordering
  useOrderEntryTool: z.boolean().nullable().optional(),
  // [Stream] Ordering
  orderingStreamDynamicPrepTime: z.boolean().nullable().optional(),
  orderingStreamTaxRate: z.number().nullable().optional(),

  // Prep times
  pickupPrepTime: z.number().min(10).nullable().optional(),
  deliveryPrepTime: z.number().min(10).nullable().optional(),
  customPrepTimeEnabled: z.boolean().optional(),
  customPrepTimeHours: CustomPrepTimeHoursSchema.optional(),

  // Holiday hours
  holidayHours: HolidayHoursConfigSchema.optional(),

  // Pickup
  pickupEnabled: z.boolean().nullable().optional(),
  pickupPaymentModuleConfig: PaymentModuleConfigSchema.optional(),
  pickupMinTotalThreshold: z.number().min(0).nullable().optional(),
  pickupMaxTotalThreshold: z.number().min(0).nullable().optional(),

  // Delivery
  deliveryEnabled: z.boolean().nullable().optional(),
  deliveryPaymentModuleConfig: PaymentModuleConfigSchema.optional(),
  deliveryVerifyAddress: z.boolean().nullable().optional(),
  deliveryLatitude: z.number().min(-90).max(90).nullable().optional(),
  deliveryLongitude: z.number().min(-180).max(180).nullable().optional(),
  deliveryFee: z.number().nullable().optional(),
  deliveryFeeConfigType: z.enum(["FLAT", "MILEAGE_TIERED"]).optional(),
  deliveryFeeConfigTiers: z
    .array(
      z.object({
        minMiles: z
          .number()
          .min(0)
          .nullable()
          .refine((v) => v !== null, "Required"),
        amount: z
          .number()
          .min(0)
          .nullable()
          .refine((v) => v !== null, "Required"),
      }),
    )
    .superRefine((tiers, ctx) => {
      const seen = new Set<number>();
      tiers.forEach((tier, index) => {
        if (tier.minMiles === null) return;
        if (seen.has(tier.minMiles)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Duplicate minimum miles value: ${tier.minMiles}`,
            path: [index, "minMiles"],
          });
        }
        seen.add(tier.minMiles);
      });
    })
    .optional(),
  deliveryRadius: z
    .number()
    .min(0.0, "Please set a delivery radius")
    .nullable()
    .optional(),
  deliveryRegionCode: z.string().nullable().optional(),
  deliveryWaiveMinimum: z.number().min(0).nullable().optional(),
  deliveryMinTotalThreshold: z.number().min(0).nullable().optional(),
  deliveryMaxTotalThreshold: z.number().min(0).nullable().optional(),
  deliveryFirstDeliveryMerchantId: z.string().nullable().optional(),
  firstDeliveryEnabled: z.boolean().nullable().optional(),
  deliveryFirstDeliveryCustomerApiKey: z.string().nullable().optional(),

  // Reservation
  reservationProvider: z.enum(["opentable"]).nullable().optional(),
  reservationEnabled: z.boolean().nullable().optional(),
  reservationExternalId: z.string().nullable().optional(),
  reservationMaxPartyThreshold: z.number().min(0).nullable().optional(),
  reservationPromptForSpecialRequest: z.boolean().nullable().optional(),

  // voicemail
  voicemailEmail: z.string().nullable().optional(),
});
